"use client";

import { useState, useEffect, useLayoutEffect, useRef, Fragment, type CSSProperties } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { signIn, getProviders } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useGooglePopupAuth } from "@/hooks/useGooglePopupAuth";
import { storeSessionForRedirect } from "@/libs/ga";
import { trackFunnel, storeAnalyticsIdentityForAuth } from "@/libs/analytics";
import LoadingSpinner from "./LoadingSpinner";
import config from "@/config";
import { MeshGradient } from "@paper-design/shaders-react";
import {
  AUTH_MODAL_HEADSHOT_PROMO_URLS,
  AUTH_MODAL_VIRAL_GALLERY_URLS,
} from "@/libs/authModalGalleryImages";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  callbackUrl?: string;
  purchaseContext?: {
    priceId: string;
    credits?: number;
    mode: string;
  };
  /** Overrides the default "Welcome to PFPMaker" / "Sign In to Continue" heading. */
  headline?: string;
  /** Overrides the default join-line under the heading. */
  subheadline?: string;
  /** Attached to auth funnel events so a non-purchase gate is distinguishable. */
  trigger?: string;
  onAuthStarted?: (method: "google" | "email") => void;
}

interface Provider {
  id: string;
  name: string;
  type: string;
  signinUrl: string;
  callbackUrl: string;
}


const SIGNIN_IMAGES = [
  "slider-1.webp",
  "7.png",
  "ai_photoshoot_promo_1.png",
  "ai_photoshoot_promo_6.png",
  "ai_photoshoot_promo_7.png",
  "change-hair-color-2.png",
  "40.png",
  "slider-2.webp",
  "65.png",
  "68.png",
  "73.png",
  "77.png",
  "ai_photoshoot_promo_3.png",
  "ai_photoshoot_promo_4.png",
  "ai_photoshoot_promo_7.png",
  "slider-5.webp",
];

/** Two identical stacks + measured loop height = seamless vertical marquee (no % rounding stitch). */
function AuthGalleryMarquee({
  imageSrcs,
  sizes,
  itemClassName,
  gapClassName,
  durationSec,
  phaseDelay,
}: {
  /** Full URLs (`https://…`) or site paths (`/assets/…`). */
  imageSrcs: string[];
  sizes: string;
  itemClassName: string;
  gapClassName: string;
  durationSec: number;
  phaseDelay: string;
}) {
  const firstSegmentRef = useRef<HTMLDivElement>(null);
  const [loopPx, setLoopPx] = useState(0);

  useLayoutEffect(() => {
    const seg = firstSegmentRef.current;
    if (!seg?.parentElement) return;

    const measure = () => {
      const parent = seg.parentElement;
      if (!parent) return;
      const segH = seg.getBoundingClientRect().height;
      const g = getComputedStyle(parent).rowGap || getComputedStyle(parent).gap || "0px";
      const gapPx = Number.parseFloat(g) || 0;
      const px = Math.round((segH + gapPx) * 100) / 100;
      if (px > 0) setLoopPx(px);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(seg);
    return () => ro.disconnect();
  }, [imageSrcs]);

  return (
    <div
      className={`absolute inset-0 flex flex-col ${gapClassName} will-change-transform`}
      style={
        loopPx > 0
          ? ({
              ["--loop-h" as string]: `${loopPx}px`,
              animation: `modalGalleryLoopPx ${durationSec}s linear infinite`,
              animationDelay: phaseDelay,
            } as CSSProperties)
          : undefined
      }
    >
      <div ref={firstSegmentRef} className={`flex flex-col ${gapClassName} shrink-0`}>
        {imageSrcs.map((src, idx) => (
          <div key={`seg-a-${idx}`} className={itemClassName}>
            <Image
              src={src}
              alt="Profile example"
              fill
              className="object-cover"
              sizes={sizes}
            />
          </div>
        ))}
      </div>
      <div className={`flex flex-col ${gapClassName} shrink-0`} aria-hidden>
        {imageSrcs.map((src, idx) => (
          <div key={`seg-b-${idx}`} className={itemClassName}>
            <Image
              src={src}
              alt="Profile example"
              fill
              className="object-cover"
              sizes={sizes}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function GlamTrustStars45() {
  const starPath =
    "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z";
  return (
    <div className="flex justify-center gap-1" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <svg key={i} className="h-6 w-6 text-amber-400 sm:h-7 sm:w-7" viewBox="0 0 20 20" fill="currentColor">
          <path d={starPath} />
        </svg>
      ))}
      <span className="relative h-6 w-6 shrink-0 sm:h-7 sm:w-7">
        <svg className="absolute inset-0 h-6 w-6 text-white/20 sm:h-7 sm:w-7" viewBox="0 0 20 20" fill="currentColor">
          <path d={starPath} />
        </svg>
        <span className="absolute inset-0 w-[50%] overflow-hidden">
          <svg className="h-6 w-6 text-amber-400 sm:h-7 sm:w-7" viewBox="0 0 20 20" fill="currentColor">
            <path d={starPath} />
          </svg>
        </span>
      </span>
    </div>
  );
}

/** Right leaf: `/assets/icons/laurel-leaf.svg`. Left leaf: same asset, mirrored horizontally. */
function GlamTrustLaurelFlank({ mirror }: { mirror?: boolean }) {
  return (
    <img
      src="/assets/icons/laurel-leaf.svg"
      alt=""
      width={56}
      height={54}
      className="block h-14 w-11 shrink-0 object-contain object-top select-none sm:h-16 sm:w-12"
      style={mirror ? { transform: "scaleX(-1)" } : undefined}
      aria-hidden
    />
  );
}

function GlamExperimentTrustFooter() {
  return (
    <div className="mt-5 flex items-stretch gap-4 border-t border-white/10 pt-6 text-center sm:mt-6 sm:gap-6 sm:pt-7">
      <div className="flex min-w-0 flex-1 flex-col items-center justify-end gap-2">
        <GlamTrustStars45 />
        <p className="max-w-[14rem] text-sm leading-snug sm:max-w-none sm:text-base">
          <span className="font-bold text-white">Rated 4.5</span>
          <span className="text-white/75">{" · "}</span>
          <span className="font-normal text-white/75">70,519 reviews</span>
        </p>
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-center justify-end gap-0">
        <div className="flex items-center justify-center gap-1 leading-none -mb-[10px] sm:gap-1.5">
          <GlamTrustLaurelFlank mirror />
          <span className="text-2xl font-bold tabular-nums leading-none text-white sm:text-4xl">18M</span>
          <GlamTrustLaurelFlank />
        </div>
        <p className="mt-0 flex items-center justify-center gap-2 text-sm leading-tight text-white/75 sm:text-base">
          <span
            className="mt-[5px] inline-block h-2.5 w-2.5 shrink-0 animate-online-dot rounded-full bg-emerald-400 sm:h-2 sm:w-2"
            aria-hidden
          />
          <span>users worldwide</span>
        </p>
      </div>
    </div>
  );
}

export default function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  callbackUrl = '/dashboard/pricing',
  purchaseContext,
  headline,
  subheadline,
  trigger,
  onAuthStarted,
}: AuthModalProps) {
  const [providers, setProviders] = useState<Record<string, Provider> | null>(null);
  const [email, setEmail] = useState("");
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [glamEmailExpanded, setGlamEmailExpanded] = useState(false);
  const glamEmailInputRef = useRef<HTMLInputElement>(null);
  const isGlamLayout = true;
  const {
    isAuthenticating: isGoogleAuthenticating,
    isSuccess: isGoogleSuccess,
    error: googleError,
    initiateGoogleAuth,
    resetAuth: resetGoogleAuth,
  } = useGooglePopupAuth();

  const authReturnUrl = () => {
    if (trigger === "free_pfp_download" && typeof window !== "undefined") {
      return `${window.location.pathname}${window.location.search}` || callbackUrl;
    }
    return callbackUrl;
  };

  // Load providers on mount
  useEffect(() => {
    const loadProviders = async () => {
      const providersData = await getProviders();
      setProviders(providersData);
    };
    loadProviders();
  }, []);

  // Track modal view
  useEffect(() => {
    if (isOpen) {
      trackFunnel("monetization", "auth_modal_shown", {
        callback_url: callbackUrl,
        has_purchase_context: !!purchaseContext,
        purchase_credits: purchaseContext?.credits,
        auth_modal_layout: "pfpmaker_layout",
        trigger: trigger || "purchase_intent",
      });
    }
  }, [isOpen, callbackUrl, purchaseContext, trigger]);

  // Handle Google auth success - show loading overlay and refresh page
  useEffect(() => {
    if (isGoogleSuccess) {
      trackFunnel('acquisition', 'signin_completed_from_modal', {
        auth_method: 'google',
        trigger: trigger || 'purchase_intent'
      });

      // Same-tab Google reload (session) + email magic-link new tab (local).
      try {
        sessionStorage.setItem('pfp_just_authenticated', 'true');
        localStorage.setItem('pfp_just_authenticated', 'true');
      } catch {
        /* private mode / disabled storage */
      }

      // Show loading overlay, then redirect to callbackUrl
      setIsReloading(true);

      setTimeout(() => {
        window.location.href = authReturnUrl();
      }, 500);
    }
  }, [isGoogleSuccess, callbackUrl, trigger]);

  useEffect(() => {
    if (!isOpen) setGlamEmailExpanded(false);
  }, [isOpen]);

  useEffect(() => {
    if (isGlamLayout && glamEmailExpanded && glamEmailInputRef.current) {
      glamEmailInputRef.current.focus();
    }
  }, [isGlamLayout, glamEmailExpanded]);

  // Set window dimensions on mount for MeshGradient
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }
  }, []);

  const handleGoogleSignIn = async () => {
    onAuthStarted?.("google");
    await initiateGoogleAuth(authReturnUrl());
  };

  const handleEmailSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;

    storeSessionForRedirect('oauth');
    storeAnalyticsIdentityForAuth();

    const returnUrl = authReturnUrl();
    onAuthStarted?.("email");
    trackFunnel('acquisition', 'signin_started', {
      auth_method: 'email',
      auth_flow: 'modal',
      callback_url: returnUrl,
      trigger: trigger || 'purchase_intent',
    });

    try {
      sessionStorage.setItem('pfp_just_authenticated', 'true');
      localStorage.setItem('pfp_just_authenticated', 'true');
    } catch {
      /* private mode / disabled storage */
    }

    setIsEmailLoading(true);
    try {
      await signIn("email", {
        email,
        callbackUrl: returnUrl,
        redirect: false
      });
      setEmailSubmitted(true);

      trackFunnel('acquisition', 'email_sent', {
        auth_method: 'email',
        auth_flow: 'modal'
      });
    } catch (error) {
      console.error("Email sign in error:", error);

      trackFunnel('acquisition', 'signin_error', {
        auth_method: 'email',
        auth_flow: 'modal',
        error: error.message || 'Unknown error'
      });
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleClose = () => {
    // Track modal dismissal
    trackFunnel("monetization", "auth_modal_dismissed", {
      has_completed_auth: isGoogleSuccess || emailSubmitted,
      dismiss_method: "close_button",
      auth_modal_layout: "pfpmaker_layout",
    });

    // Reset states
    resetGoogleAuth();
    setEmailSubmitted(false);
    setEmail("");
    setGlamEmailExpanded(false);
    onClose();
  };

  // Email submitted state
  if (emailSubmitted) {
    return (
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0" style={{ backgroundColor: '#5956E9' }}>
              {/* Animated mesh gradient background */}
              {dimensions.width > 0 && (
                <MeshGradient
                  width={dimensions.width}
                  height={dimensions.height}
                  colors={["#5956E9", "#7c3aed", "#a855f7", "#F163C2"]}
                  distortion={0.8}
                  swirl={0.1}
                  grainMixer={0}
                  grainOverlay={0.1}
                  speed={0.65}
                />
              )}
              {/* Dark overlay on top of gradient */}
              <div className="absolute inset-0 bg-black/20" />
            </div>
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 text-center shadow-2xl transition-all">
                  {/* Close button */}
                  <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Email Icon */}
                  <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-6">
                    <svg className="h-10 w-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Check your email</h3>
                  <p className="text-gray-600 mb-2">
                    We've sent a magic link to <strong>{email}</strong>
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    {trigger === "free_pfp_download"
                      ? "Click the link in the email to sign in and download your profile picture."
                      : "Click the link in the email to sign in and complete your purchase."}
                  </p>

                  {/* Spam folder reminder */}
                  <div className="inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 mb-4">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-sm font-medium text-yellow-800">Check Spam folder!</span>
                  </div>

                  <p className="text-xs text-gray-500">
                    {trigger === "free_pfp_download"
                      ? "Your profile picture is saved. After you sign in, it will download automatically — even if the link opens in a new tab."
                      : "Keep this tab open. After signing in, you'll be automatically directed to complete your purchase."}
                  </p>

                  <button
                    onClick={() => {
                      setEmailSubmitted(false);
                      setEmail("");
                    }}
                    className="mt-4 text-primary hover:text-primary/80 text-sm font-medium"
                  >
                    ← Back to sign in
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  // Main auth modal
  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0" style={{ backgroundColor: "#5956E9" }}>
              {dimensions.width > 0 && (
                <MeshGradient
                  width={dimensions.width}
                  height={dimensions.height}
                  colors={["#5956E9", "#7c3aed", "#a855f7", "#F163C2"]}
                  distortion={0.8}
                  swirl={0.1}
                  grainMixer={0}
                  grainOverlay={0.1}
                  speed={0.65}
                />
              )}
              <div className={`absolute inset-0 ${isGlamLayout ? "bg-black/55" : "bg-black/20"}`} />
            </div>
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
              <div className="flex w-full flex-col items-center gap-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel
                    className={`w-full transform overflow-hidden shadow-2xl transition-all ${
                      isGlamLayout
                        ? "max-w-6xl rounded-[28px] border border-white/10 bg-black"
                        : "max-w-5xl rounded-2xl bg-white"
                    }`}
                  >
                    <div className={`flex flex-col lg:flex-row ${isGlamLayout ? "min-h-[680px]" : "min-h-[465px] md:min-h-[600px]"}`}>
                      {isGlamLayout ? (
                        <div className="hidden lg:block lg:w-[54%] bg-black relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/75 z-[2]" />
                          <div className="absolute inset-0 p-6">
                            <div className="grid h-full grid-cols-3 gap-3 [mask-image:linear-gradient(to_bottom,transparent_0%,black_8%,black_92%,transparent_100%)]">
                              {Array.from({ length: 3 }).map((_, col) => {
                                const pool =
                                  col === 2 ? AUTH_MODAL_HEADSHOT_PROMO_URLS : AUTH_MODAL_VIRAL_GALLERY_URLS;
                                const stack = Array.from({ length: 24 }).map((__, idx) => {
                                  return pool[idx % pool.length];
                                });
                                return (
                                  <div
                                    key={`desk-col-${col}`}
                                    className="relative h-full overflow-hidden rounded-[20px] bg-black/40"
                                  >
                                    <AuthGalleryMarquee
                                      imageSrcs={stack}
                                      sizes="(max-width: 1280px) 14vw, 11vw"
                                      itemClassName="relative w-full aspect-[3/4] shrink-0 overflow-hidden rounded-2xl bg-white/10"
                                      gapClassName="gap-2"
                                      durationSec={95}
                                      phaseDelay={col === 1 ? "-7s" : "0s"}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden p-8">
                          <div className="absolute inset-0 bg-black/20" />
                          <div className="absolute inset-0 grid grid-cols-4 gap-4 p-8 opacity-40">
                            {Array.from({ length: 16 }).map((_, i) => (
                              <div
                                key={i}
                                className="bg-white/20 rounded-2xl aspect-square backdrop-blur-sm overflow-hidden relative"
                                style={{
                                  transform: `rotate(${(i % 4) * 5 - 10}deg)`,
                                  animationDelay: `${i * 0.2}s`,
                                  animationDuration: `${2 + (i % 3)}s`,
                                }}
                              >
                                <Image
                                  src={`/assets/images/signin/${SIGNIN_IMAGES[i]}`}
                                  alt="Profile Picture"
                                  fill
                                  className="object-cover rounded-2xl"
                                  sizes="25vw"
                                />
                              </div>
                            ))}
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-white text-center px-8 max-w-2xl relative z-10">
                              <div className="grid grid-cols-2 gap-8 mb-8">
                                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                                  <div className="text-5xl font-bold mb-2">18M+</div>
                                  <div className="text-white/80 text-sm font-medium">Users Worldwide</div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                                  <div className="text-5xl font-bold mb-2">55M+</div>
                                  <div className="text-white/80 text-sm font-medium">Photos Created</div>
                                </div>
                              </div>
                              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2 border border-white/20 max-w-md">
                                <div className="flex items-start gap-4 mb-4">
                                  <img
                                    src="/assets/images/signin/Maggie_review.png"
                                    alt="Maggie"
                                    className="w-12 h-12 rounded-full object-cover border-2 border-white/20"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-white font-semibold">Maggie</span>
                                      <div className="flex gap-0.5">
                                        {[...Array(5)].map((_, i) => (
                                          <svg key={i} className="w-4 h-4 text-yellow-300 fill-current" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                          </svg>
                                        ))}
                                      </div>
                                    </div>
                                    <p className="text-white/90 text-sm leading-relaxed text-left">
                                      This worked so fast and so well I&apos;m still shocked. It didn&apos;t edit my face or anything else like other AIs out there I&apos;ll be using some photos I picked for my CV!
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className={`flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 relative ${isGlamLayout ? "bg-black text-white" : "bg-gray-50"}`}>
                        <button
                          onClick={handleClose}
                          className={`absolute top-4 right-4 transition-colors z-20 ${isGlamLayout ? "text-white/70 hover:text-white" : "text-gray-400 hover:text-gray-600"}`}
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>

                        <div className={`max-w-md w-full ${isGlamLayout ? "space-y-5 py-6 sm:py-8" : "space-y-8 pt-8 pb-4"}`}>
                          {isGlamLayout && (
                            <div className="lg:hidden relative h-64 overflow-hidden rounded-[22px] border border-white/10 bg-black/40">
                              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black z-[2]" />
                              <div className="absolute inset-0 p-2">
                                <div className="grid h-full grid-cols-3 gap-2 [mask-image:linear-gradient(to_bottom,transparent_0%,black_8%,black_92%,transparent_100%)]">
                                  {Array.from({ length: 3 }).map((_, col) => {
                                    const pool =
                                      col === 2 ? AUTH_MODAL_HEADSHOT_PROMO_URLS : AUTH_MODAL_VIRAL_GALLERY_URLS;
                                    const stack = Array.from({ length: 20 }).map((__, idx) => {
                                      return pool[idx % pool.length];
                                    });
                                    return (
                                      <div
                                        key={`mobile-col-${col}`}
                                        className="relative h-full overflow-hidden rounded-2xl bg-black/30"
                                      >
                                        <AuthGalleryMarquee
                                          imageSrcs={stack}
                                          sizes="28vw"
                                          itemClassName="relative w-full aspect-[3/4] shrink-0 overflow-hidden rounded-xl bg-white/10"
                                          gapClassName="gap-1.5"
                                          durationSec={95}
                                          phaseDelay={col === 1 ? "-6s" : "0s"}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="text-center">
                            {isGlamLayout && (
                              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-[radial-gradient(circle_at_30%_35%,#fef08a_0%,#fb7185_35%,#a855f7_65%,#60a5fa_100%)] p-[5px]">
                                <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-black">
                                  {/*
                                  <img
                                    src="/assets/logomark.svg"
                                    alt=""
                                    width={40}
                                    height={40}
                                    className="h-10 w-10 shrink-0"
                                    aria-hidden
                                  />
                                  */}
                                </div>
                              </div>
                            )}
                            <h2 className={`font-bold ${isGlamLayout ? "text-white text-3xl leading-tight" : "text-3xl text-gray-900"}`}>
                              {headline || (isGlamLayout ? "Welcome to PFPMaker" : "Sign In to Continue")}
                            </h2>
                            {purchaseContext && purchaseContext.credits && (
                              <p className={`mt-2 font-medium ${isGlamLayout ? "text-amber-300" : "text-primary"}`}>
                                Complete your purchase of {purchaseContext.credits} credits
                              </p>
                            )}
                            <p className={`mt-2 ${isGlamLayout ? "text-white/70" : "text-gray-600"}`}>
                              {subheadline ||
                                (isGlamLayout
                                  ? "Join 18M+ users creating scroll-stopping profile photos"
                                  : "Join 18M+ users creating stunning profile pictures")}
                            </p>
                            {isGlamLayout && !headline && (
                              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                                <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs">1, 100+ AI styles</span>
                                <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs">60+ Tools</span>
                              </div>
                            )}
                          </div>

                          <div className="space-y-4">
                            {!providers && (
                              <div className="flex items-center justify-center py-8">
                                <LoadingSpinner size="sm" text="" fullScreen={false} />
                              </div>
                            )}

                            {providers?.google && (
                              <button
                                onClick={handleGoogleSignIn}
                                disabled={isGoogleAuthenticating}
                                className={`w-full flex items-center justify-center px-4 py-3 rounded-2xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                                  isGlamLayout
                                    ? "bg-white hover:bg-white/90 text-black border border-white"
                                    : "bg-gray-900 hover:bg-gray-800 text-white"
                                }`}
                              >
                                {isGoogleAuthenticating ? (
                                  <>
                                    <LoadingSpinner size="sm" text="" fullScreen={false} />
                                    <span className="ml-3">Signing in with Google...</span>
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    Continue with Google
                                  </>
                                )}
                              </button>
                            )}

                            {googleError && (
                              <div className={`${isGlamLayout ? "bg-red-500/20 border-red-300/40 text-red-100" : "bg-red-50 border-red-200 text-red-700"} border px-4 py-3 rounded-lg text-sm`}>
                                {googleError}
                              </div>
                            )}

                            {providers?.email &&
                              (isGlamLayout && !glamEmailExpanded ? (
                                <button
                                  type="button"
                                  onClick={() => setGlamEmailExpanded(true)}
                                  className="flex w-full items-center justify-center rounded-2xl border border-white bg-white px-4 py-3 font-medium text-black transition-colors duration-200 hover:bg-white/90"
                                >
                                  <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  Continue with Email
                                </button>
                              ) : (
                                <form onSubmit={handleEmailSignIn} className="space-y-3">
                                  <input
                                    ref={isGlamLayout ? glamEmailInputRef : undefined}
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    className={`w-full rounded-2xl px-4 py-3 outline-none transition-colors ${
                                      isGlamLayout
                                        ? "border border-white/30 bg-black text-white placeholder:text-white/50 focus:border-white/80"
                                        : "border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary"
                                    }`}
                                  />
                                  <button
                                    type="submit"
                                    disabled={isEmailLoading || !email}
                                    className={`flex w-full items-center justify-center rounded-2xl px-4 py-3 font-medium transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
                                      isGlamLayout
                                        ? "border border-white bg-white text-black hover:bg-white/90"
                                        : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                    }`}
                                  >
                                    <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    {isEmailLoading ? "Sending..." : "Continue with Email"}
                                  </button>
                                </form>
                              ))}

                            <div className={`text-center text-[0.65rem] ${isGlamLayout ? "text-white/65" : "text-gray-500"}`}>
                              By continuing, I acknowledge the{" "}
                              <Link
                                href="https://pfpmaker.com/privacy-policy"
                                target="_blank"
                                className="text-inherit underline underline-offset-[3px] decoration-current/50 hover:decoration-current"
                              >
                                Privacy Policy
                              </Link>{" "}
                              and agree to the{" "}
                              <Link
                                href="https://pfpmaker.com/terms-of-service"
                                target="_blank"
                                className="text-inherit underline underline-offset-[3px] decoration-current/50 hover:decoration-current"
                              >
                                Terms of Use
                              </Link>
                              . I also confirm that I am at least 18 years old.
                            </div>

                            {isGlamLayout && <GlamExperimentTrustFooter />}
                          </div>

                          {!isGlamLayout && (
                            <div className="mt-6 lg:hidden rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
                              <div className="flex items-start gap-3">
                                <img
                                  src="/assets/images/signin/Maggie_review.png"
                                  alt="Maggie"
                                  className="h-10 w-10 shrink-0 rounded-full border border-gray-200 object-cover shadow-sm"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="mb-1 flex items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-900">Maggie</span>
                                    <div className="flex gap-0.5">
                                      {[...Array(5)].map((_, i) => (
                                        <svg
                                          key={i}
                                          className="h-3.5 w-3.5 fill-current text-amber-400"
                                          viewBox="0 0 20 20"
                                        >
                                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                      ))}
                                    </div>
                                  </div>
                                  <p className="text-left text-xs leading-relaxed text-gray-600">
                                    This worked so fast and so well I&apos;m still shocked. It didn&apos;t edit my face or
                                    anything else like other AIs out there I&apos;ll be using some photos I picked for my CV!
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      <style jsx global>{`
        @keyframes modalGalleryLoopPx {
          0% {
            transform: translate3d(0, 0, 0);
          }
          100% {
            transform: translate3d(0, calc(-1 * var(--loop-h, 0px)), 0);
          }
        }
      `}</style>

    {/* Full-screen loading overlay - shown after successful auth while page reloads */}
    {isReloading && (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center" style={{ backgroundColor: 'rgb(0 0 0 / 90%)' }}>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
            <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Successfully Signed In!</h3>
          <p className="text-white/80">Loading your dashboard...</p>
          <div className="mt-6">
            <LoadingSpinner size="md" text="" fullScreen={false} />
          </div>
        </div>
      </div>
    )}
    </>
  );
}

